import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { SpecimenLabel } from "./SpecimenLabel";

interface DiffCardProps {
  before: string;
  after: string;
  beforeLabel?: string;
  afterLabel?: string;
  className?: string;
}

export const DiffCard = ({ 
  before, 
  after, 
  beforeLabel = "BEFORE", 
  afterLabel = "AFTER",
  className = "" 
}: DiffCardProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <div ref={ref} className={`grid md:grid-cols-2 gap-8 md:gap-12 ${className}`}>
      {/* Before - Raw testimonial */}
      <motion.div
        className="relative"
        initial={{ opacity: 0, x: -20 }}
        animate={isInView ? { opacity: 1, x: 0 } : {}}
        transition={{ duration: 0.6 }}
      >
        <SpecimenLabel text={beforeLabel} className="mb-3 block" />
        <div className="ink-border rounded-sm p-6 bg-muted/30">
          <p className="text-muted-foreground italic font-editorial text-lg leading-relaxed">
            "{before}"
          </p>
        </div>
        {/* Extraction marks */}
        <motion.div
          className="absolute -right-2 top-1/2 w-4 h-px bg-brand-600/40"
          initial={{ scaleX: 0 }}
          animate={isInView ? { scaleX: 1 } : {}}
          transition={{ delay: 0.4, duration: 0.3 }}
          style={{ transformOrigin: "left" }}
        />
      </motion.div>

      {/* Thread connector (visible on md+) */}
      <motion.div
        className="hidden md:block absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-px"
        initial={{ scaleX: 0 }}
        animate={isInView ? { scaleX: 1 } : {}}
        transition={{ delay: 0.5, duration: 0.4 }}
      >
        <div className="h-full w-full bg-gradient-to-r from-brand-600/40 via-brand-500/60 to-brand-600/40" />
      </motion.div>

      {/* After - Transformed content */}
      <motion.div
        className="relative"
        initial={{ opacity: 0, x: 20 }}
        animate={isInView ? { opacity: 1, x: 0 } : {}}
        transition={{ delay: 0.3, duration: 0.6 }}
      >
        <SpecimenLabel text={afterLabel} className="mb-3 block" />
        <div className="border border-brand-200 rounded-sm p-6 bg-background shadow-sm">
          <motion.p
            className="text-foreground text-lg leading-relaxed"
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ delay: 0.8, duration: 0.6 }}
          >
            {after}
          </motion.p>
        </div>
        {/* Extraction marks */}
        <motion.div
          className="absolute -left-2 top-1/2 w-4 h-px bg-brand-600/40"
          initial={{ scaleX: 0 }}
          animate={isInView ? { scaleX: 1 } : {}}
          transition={{ delay: 0.6, duration: 0.3 }}
          style={{ transformOrigin: "right" }}
        />
      </motion.div>
    </div>
  );
};
